import * as THREE from 'three'
import React, { useMemo } from 'react'
import {useThree } from '@react-three/fiber';

import { delModel, extrudeSettings, roofPattern, textureAnisotropy } from '../../../../../../../../utils/Functions';
import { const_var, params } from '../../../../../../../../redux/reducers/reducer';
import { getSimilarTexture, textureAppliedFor } from '../../../../../../../../redux/reducers/utils';
import { CSG } from '../../../../../../../../assets/libs/csg-lib';
import { MaterialInnerWall, MaterialOuterWallNew, MaterialWainScotWallNew } from '../../../../../../CustomMaterial';

const WallModel = () => {
    const { gl } = useThree ();

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false

    const wallThickness = 0.005;
    const offsetWithDoorThickness = 0.127;
    const isEqualHeight = (params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) == params.p_h) ? 0.2 : 0;

    let offset;
    
    const textureObj = useMemo(() => {
        let similarSidingTextures = getSimilarTexture(params.wallTexture)
        let similarWainscotTextures = getSimilarTexture(params.wainscotTexture)

        const texture = const_var.wallTextureObj?.[similarSidingTextures]?.clone();
        const wainscotTexture = const_var.wallTextureObj?.[similarWainscotTextures]?.clone();

        const metalTexture = const_var.wallTextureObj?.["vertical"]?.clone();
        const lightTexture = const_var.wallTextureObj?.["vertical_light"]?.clone();
        
        if(metalTexture){
            if (params.wallOrientation == "vertical") metalTexture.rotation = Math.PI/-2;
            else metalTexture.rotation = 0;
        }
        if(lightTexture) lightTexture.rotation = metalTexture.rotation;
        if(texture && const_var.selSidingOption === "metal") texture.rotation = metalTexture.rotation
        if(wainscotTexture && (params.wainscotTexture === "none" || params.wainscotTexture === "vertical")) wainscotTexture.rotation = metalTexture.rotation

        return {"texture":texture,"wainscotTexture" : wainscotTexture,"metalTexture":metalTexture,"lightTexture":lightTexture};
    }, [params.p_v_w, params.wallTexture, const_var.wallTextureObj, const_var.selSidingOption, params.wallOrientation,params.wainscotTexture]);

    const texture = useMemo(() => {
        if(const_var.metalTexture){
            let texture = const_var.metalTexture.clone();
            textureAnisotropy(gl, texture,1.3, 1.3, (params.p_v_w != true) ? 0 :  Math.PI/-2);    
            return texture
        }else{
            return null
        }
    }, [params.p_v_w,params.wallTexture,const_var.wallTextureObj[params.wallTexture]])

    const legDistance = useMemo(() => {
        return (const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined) ? const_var.DistanceArr[params.p_b_t][params.p_r_s] : 5
    }, [const_var.DistanceArr, params.p_b_t, params.p_r_s,params.fourth_center_cost,const_var.DistanceArr.length>0 && const_var.DistanceArr[params.p_b_t][params.p_r_s]!=undefined && const_var.DistanceArr[params.p_b_t][params.p_r_s]])

    if(params.lean_p_d % legDistance === 0) offset = 0.15;
    else offset = 0;

    // left Lean Front storage Wall - CSG
    const storageFrontWallModelObj = useMemo(() => {
        if(!params.add_storage_check) return;

        let mainWall;
        let innerWall;
        let wainscotWall;

        const storageFrontWallModel = new THREE.Shape();

        // if (params.p_b_c_b_l_f !== 'Open' || typeof(params.p_b_c_b_l_f) === 'object') {
            if (params.p_r_s === 1) {
                storageFrontWallModel.moveTo(-(params.lean_p_w - wallThickness), 0);
                storageFrontWallModel.lineTo(-(params.lean_p_w - wallThickness), params.lean_p_h);
                storageFrontWallModel.lineTo(-(params.lean_p_w - wallThickness), params.lean_p_h + const_var.roofRegularDst);
                storageFrontWallModel.quadraticCurveTo(-(params.lean_p_w - wallThickness), params.lean_p_h + const_var.roofRegularDst * 1.8 + (const_var.roofRegularDst * params.b_l_t_r_p / 12) / 10, 
                                                -(params.lean_p_w - wallThickness - const_var.roofRegularDst + 0.1), params.lean_p_h + const_var.roofRegularDst * (2 + params.b_l_t_r_p / 12))
                storageFrontWallModel.lineTo(0, params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w - wallThickness) + const_var.roofRegularDst * (2)));
                storageFrontWallModel.lineTo(0, 0);
                storageFrontWallModel.closePath();
            } else {
                storageFrontWallModel.moveTo(-params.lean_p_w + offsetWithDoorThickness - 0.12, params.lean_p_h);
                storageFrontWallModel.lineTo(0, params.lean_p_h + params.lean_p_w / 12 * params.b_l_t_r_p - isEqualHeight);
                storageFrontWallModel.lineTo(0, 0);
                storageFrontWallModel.lineTo(-params.lean_p_w + offsetWithDoorThickness - 0.12, 0);
                storageFrontWallModel.closePath();
            }
        // }
                
        let verticalHorizontalPosStorageF = {"x": params.p_w / -2  - params.lean_p_w,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness + 0.03}
        let verticalHorizontalRotStorageF = {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let patternLengthStorageF = params.lean_p_h + ((params.b_l_t_r_p / 12) * (params.lean_p_w)) + (params.p_w_c_n ? -2.935 : 0) + (params.p_r_s === 1 ? 3 : 0)
        let patternDepthStorageF = params.lean_p_w
    
        const meshStorageWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLengthStorageF, isPattern), extrudeSettings(patternDepthStorageF))) 
        const meshFrontStorageWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageFrontWallModel, extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageInnerWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(storageFrontWallModel, extrudeSettings(wallThickness)), new THREE.MeshStandardMaterial({ color: 'brown' }))
        const meshFrontStorageDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanStorageFront', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'green' }))
        const meshSideDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('leftLeanSide', 'wall'), extrudeSettings(1)), new THREE.MeshStandardMaterial({ color: 'red' }));
    
        let meshStorageWallModel_Temp = meshStorageWallModel.clone();
        meshStorageWallModel_Temp.position.set(verticalHorizontalPosStorageF.x,verticalHorizontalPosStorageF.y,verticalHorizontalPosStorageF.z);
        meshStorageWallModel_Temp.rotation.set(verticalHorizontalRotStorageF.x,verticalHorizontalRotStorageF.y,verticalHorizontalRotStorageF.z);
        meshStorageWallModel_Temp.updateMatrixWorld();
    
        let meshFrontStorageWallModel_Temp = meshFrontStorageWallModel.clone();
        meshFrontStorageWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 + parseInt(params.add_storage) - 0.5)
        meshFrontStorageWallModel_Temp.updateMatrixWorld();
        
        let meshFrontStorageInnerWallModel_Temp = meshFrontStorageInnerWallModel.clone();
        meshFrontStorageInnerWallModel_Temp.position.set(-params.p_w / 2, 0, -params.lean_p_d / 2 + parseInt(params.add_storage))
        meshFrontStorageInnerWallModel_Temp.updateMatrixWorld();

        let meshFrontStorageDelModel_Temp = meshFrontStorageDelModel.clone();
        meshFrontStorageDelModel_Temp.position.set(0, 0, parseInt(params.add_storage) - 0.5 -params.lean_p_d / 2);
        meshFrontStorageDelModel_Temp.updateMatrixWorld();

        let meshSideDelModel_Temp = meshSideDelModel.clone();
        meshSideDelModel_Temp.position.set(-params.p_w / 2 - params.lean_p_w - 0.75, 0, -params.lean_p_pos - params.leftLeanStoragePosition);
        meshSideDelModel_Temp.rotateY(Math.PI / 2)
        meshSideDelModel_Temp.updateMatrixWorld();
    
        const meshFrontStorageWallModel_CSG = CSG.fromMesh(meshFrontStorageWallModel_Temp);
        const meshFrontStorageInnerWallModel_CSG = CSG.fromMesh(meshFrontStorageInnerWallModel_Temp);
        const meshFrontStorageDelModel_CSG = CSG.fromMesh(meshFrontStorageDelModel_Temp);
        const meshStorageWallModel_CSG = CSG.fromMesh(meshStorageWallModel_Temp);
        const meshSideDelModel_CSG = CSG.fromMesh(meshSideDelModel_Temp);
    
        const frontStorageWallModel_CSG =  meshFrontStorageWallModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const frontStorageInnerWallModel_CSG =  meshFrontStorageInnerWallModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const storageSideInnerWallDelModel_CSG = frontStorageInnerWallModel_CSG.subtract(meshSideDelModel_CSG);

        const storageWallModel_CSG = meshStorageWallModel_CSG.intersect(frontStorageWallModel_CSG);
        const storageSideWallDelModel_CSG = storageWallModel_CSG.subtract(meshSideDelModel_CSG);

        const CSGFrontStorageWallModel = CSG.toMesh(storageSideWallDelModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))
        const CSGFrontStorageInnerWallModel = CSG.toMesh(storageSideInnerWallDelModel_CSG, new THREE.Matrix4(), new THREE.MeshStandardMaterial({ color: 'blue' }))

        mainWall = CSGFrontStorageWallModel.geometry;
        innerWall = CSGFrontStorageInnerWallModel.geometry;

            /************** cutting hole in wainscot using CSG for Storage Front***************/
        let wainscotVerticalHorizontalPosStorageF = {"x": params.p_w / -2  - params.lean_p_w,"y" : 0, "z" : params.lean_p_d / -2 + parseInt(params.add_storage) + wallThickness + 0.03}
        let wainscotVerticalHorizontalRotStorageF = {"x":0,"y" : Math.PI / 2,"z" : Math.PI / 2}
        let lengthPatternStorageF = 3
        let depthPatternStorageF = patternDepthStorageF

        const meshStorageFrontWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPatternStorageF, isPattern), extrudeSettings(depthPatternStorageF))) 
        let meshStorageFrontWainscotModel_Temp = meshStorageFrontWainscotModel.clone();
        meshStorageFrontWainscotModel_Temp.position.set(wainscotVerticalHorizontalPosStorageF.x,wainscotVerticalHorizontalPosStorageF.y,wainscotVerticalHorizontalPosStorageF.z);
        meshStorageFrontWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRotStorageF.x,wainscotVerticalHorizontalRotStorageF.y,wainscotVerticalHorizontalRotStorageF.z);
        meshStorageFrontWainscotModel_Temp.updateMatrixWorld();

        const meshStorageFrontWainscotModel_CSG = CSG.fromMesh(meshStorageFrontWainscotModel_Temp);

        const storageFrontWainscotModel_CSG = meshStorageFrontWainscotModel_CSG.subtract(meshFrontStorageDelModel_CSG);
        const storageSideDelModel_CSG = storageFrontWainscotModel_CSG.subtract(meshSideDelModel_CSG);

        const CSGStorageFrontWainscotModel = CSG.toMesh(
            storageSideDelModel_CSG,
            new THREE.Matrix4()
        )

        wainscotWall = CSGStorageFrontWainscotModel.geometry;

        return {mainWall, innerWall, wainscotWall}

    }, [params.wallTexture,params.b_l_t_r_p, params.add_storage, params.leftLeanStoragePosition, params.p_v_w, params.p_w, params.lean_p_d, params.p_b_c_b_l_f, params.lean_p_pos, params.p_r_s, params.lean_p_w, params.b_l_t_r_p, params.lean_p_h,const_var.newDoorsArray, params.add_storage_check, isPattern]);

    /*******************************  ****************************/

    return (
        <>
            {params.add_storage_check && storageFrontWallModelObj?.mainWall &&
                <mesh name="L_L_F_S_W" geometry={storageFrontWallModelObj?.mainWall} position={[0, 0, offset]} renderOrder={1}>
                    <MaterialOuterWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
            {params.add_storage_check && storageFrontWallModelObj?.innerWall &&
                <mesh name="storage" geometry={storageFrontWallModelObj?.innerWall} position={[0, 0, -wallThickness + offset - 0.01]}> 
                    <MaterialInnerWall texture={texture} visibleSection={"leftLean"} />
                </mesh>
            }
            {/* Storage Front Wainscot */}
            {params.p_w_c_n == true && params.add_storage_check && storageFrontWallModelObj?.wainscotWall &&
                <mesh name="L_L_F_S_W_wainscot" geometry={storageFrontWallModelObj?.wainscotWall} position={[0,0,0.005 + offset]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"leftlean"} />
                </mesh>
            }
        </>
    )
}

export default WallModel